<template>
    <div>
        <table class="w-full text-xs border" v-if="observations.length > 0">
            <template v-if="showHeaders">
                <thead class="table-header-group font-bold">
                    <tr class="border">
                        <td class="p-1" style="width: 5em">{{ $t('commons.index') }}</td>
                        <td class="p-1" style="width: 5em">{{ $t('commons.phase') }}</td>
                        <td class="p-1">{{ $t('project.editions.observationsReports.title') }}</td>
                        <td class="p-1" width="20%">{{ $t('commons.status') }}</td>
                        <td
                            class="p-1"
                            width="20px"
                            v-if="$route.name === 'followByBundle' || $route.name === 'followConceptionByBundle'"
                        ></td>
                    </tr>
                </thead>
            </template>
            <tbody>
                <tr class="border border-b-2 border-b-black" v-if="theme || !showHeaders">
                    <td colspan="4" class="p-1 pt-2">
                        <h3 class="font-bold" v-if="theme">{{ theme }}</h3>
                        <h3 class="font-italic" v-else>
                            {{ $t('project.editions.observationsReports.noTheme') }}
                        </h3>
                    </td>
                </tr>
                <template v-for="observation in observations">
                    <tr
                        class="border"
                        :class="{
                            'new-observation': humanizeDate(observation.reportedAt) === humanizeDate(meetingDate),
                        }"
                    >
                        <td class="p-1 align-top">
                            <div class="flex justify-start relative">
                                <a :id="'uuid_' + observation.id" style="top: -160px" class="absolute"></a>
                                <router-link
                                    :to="{
                                        name:
                                            $route.name === 'followConceptionByBundle'
                                                ? 'followConceptionByBundleObservation'
                                                : $route.name === 'followByBundle'
                                                ? 'followByBundleObservation'
                                                : 'followObservation',
                                        params: { ...$route.params, observationId: observation.id },
                                    }"
                                >
                                    <div
                                        v-if="!observation.footprint || observation.obsoleteAt"
                                        class="p-1 px-2 border-black"
                                        :class="{
                                            [getObservationClass(observation)]: true,
                                            border:
                                                observation.type === 'other' ||
                                                observation.type === 'general' ||
                                                observation.type === 'administrative',
                                        }"
                                    >
                                        {{ (observation.index + 1).toString().padStart(3, '0') }}
                                    </div>
                                    <app-static-marker
                                        v-else
                                        size="mini"
                                        class="text-xs cursor-pointer"
                                        :observation="observation"
                                    ></app-static-marker>
                                </router-link>
                            </div>
                        </td>
                        <td class="p-1 align-top">
                            <hsc-menu-style-white>
                                <hsc-menu-button-menu>
                                    <template slot="contextmenu">
                                        <hsc-menu-item
                                            :label="$t('commons.phases.CON')"
                                            v-if="isCONAllowed"
                                            :checked="observation.phase === 'CON'"
                                            @click="savePhase(observation, 'CON')"
                                        />
                                        <hsc-menu-item
                                            :label="$t('commons.phases.EXE')"
                                            v-if="isEXEAllowed"
                                            :checked="observation.phase === 'EXE'"
                                            @click="savePhase(observation, 'EXE')"
                                        />
                                        <hsc-menu-item
                                            :label="$t('commons.phases.OPR')"
                                            v-if="isOPRAllowed"
                                            :checked="observation.phase === 'OPR'"
                                            @click="savePhase(observation, 'OPR')"
                                        />
                                        <hsc-menu-item
                                            :label="$t('commons.phases.Receipt')"
                                            v-if="isOPRAllowed"
                                            :checked="observation.phase === 'Receipt'"
                                            @click="savePhase(observation, 'Receipt')"
                                        />
                                        <hsc-menu-item
                                            :label="$t('commons.phases.OPL')"
                                            v-if="isOPLAllowed"
                                            :checked="observation.phase === 'OPL'"
                                            @click="savePhase(observation, 'OPL')"
                                        />
                                        <hsc-menu-item
                                            :label="$t('commons.phases.Delivery')"
                                            v-if="isOPLAllowed"
                                            :checked="observation.phase === 'Delivery'"
                                            @click="savePhase(observation, 'Delivery')"
                                        />
                                        <hsc-menu-item
                                            :label="$t('commons.phases.APA')"
                                            v-if="isAPAAllowed"
                                            :checked="observation.phase === 'APA'"
                                            @click="savePhase(observation, 'APA')"
                                        />
                                    </template>
                                    <button class="hover:underline">
                                        {{ $t('commons.phases.' + observation.phase) }}
                                    </button>
                                </hsc-menu-button-menu>
                            </hsc-menu-style-white>
                        </td>
                        <td class="p-1">
                            <div class="w-full flex flex-col cursor-pointer hover:underline">
                                <div class="flex flex-col justify-start" @click="editTitle(observation)">
                                    <span
                                        v-html="
                                            observation.title.startsWith('<p>')
                                                ? observation.title.replace(
                                                      '<p>',
                                                      '<p>' + humanizeDate(observation.reportedAt) + ' ',
                                                  )
                                                : humanizeDate(observation.reportedAt) + ' ' + observation.title
                                        "
                                        class="whitespace-pre-wrap html-breakline observation-title"
                                    />
                                    <div
                                        class="w-full"
                                        v-if="observation.attachments && observation.attachments.length > 0"
                                    >
                                        <template v-for="attachment of observation.attachments">
                                            <div
                                                style="min-height: 400px; max-height: 400px; page-break-inside: avoid"
                                                v-if="isImage(attachment.url)"
                                            >
                                                <app-leaflet-viewer
                                                    class="flex-grow"
                                                    :src="attachment.url"
                                                    :shapes="attachment.shapes"
                                                    :preview="true"
                                                    style="min-height: 400px"
                                                />
                                            </div>
                                            <div v-else class="flex items-center">
                                                <icon-file-document-outline />
                                                <a class="undeline" :href="`https://app.tex.moe${attachment.url}`">
                                                    {{ `https://app.tex.moe${attachment.url}` }}
                                                </a>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                <div class="ml-4 text-blue-500" v-if="observation.room">
                                    {{ observation.room.fullName }}
                                </div>
                                <div class="ml-4 text-blue-500" v-else-if="observation.zone">
                                    {{ observation.zone.fullName }}
                                </div>
                            </div>
                        </td>
                        <td
                            class="p-1 align-top"
                            width="20%"
                            :class="{
                                'font-bold text-red-500': observation.isLate && !observation.isEmitted,
                            }"
                        >
                            <app-date-link
                                v-model="observation.dueDate"
                                :class="{ 'hover:underline': !observation.resolvedAt }"
                                :disabled="!!observation.resolvedAt"
                                @input="saveDueDate(observation)"
                            >
                                <template v-slot:default>{{ observation.label }}</template>
                            </app-date-link>
                        </td>
                        <td
                            v-if="$route.name === 'followByBundle' || $route.name === 'followConceptionByBundle'"
                            class="p-1 align-top"
                        >
                            <app-button
                                size="mini"
                                icon="icon-check"
                                class="bg-green-500 text-white"
                                :title="$t('project.follow.observation.resolve')"
                                @click="onResolve(observation)"
                            ></app-button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import AppStaticMarker from '@/components/appStaticMarker/AppStaticMarker.vue';
import AppLeafletViewer from '@/components/appLeafletViewer/AppLeafletViewer.vue';

import { isImage } from '@/services/file.service';
import { getObservationClass, updateObservation } from '@/features/observations/observation.service';
import { humanizeDate } from '@/filters/dateFilter';
import { queryProject } from '@/features/projects/projects.service';
import AppDateLink from '@/components/appDateLink/AppDateLink.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import AppTextEditor from '@/components/appTextEditor/AppTextEditor.vue';
import AppPopup from '@/components/app-popup/AppPopup.vue';
export default {
    props: {
        meetingDate: { type: Date },
        theme: { type: String },
        showHeaders: { type: Boolean },
        observations: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    components: { AppPopup, AppTextEditor, AppTextarea, AppButton, AppDateLink, AppLeafletViewer, AppStaticMarker },
    methods: {
        getObservationClass,
        isImage,
        humanizeDate,
        editTitle(observation) {
            this.$emit('editTitle', observation);
        },
        savePhase(observation, phase) {
            return updateObservation(this.$route.params.projectId, { id: observation.id, phase });
        },
        saveDueDate(observation) {
            const patch = {
                id: observation.id,
                dueDate: observation.dueDate,
            };
            return updateObservation(this.$route.params.projectId, patch);
        },
        onResolve(observation) {
            const patch = {
                id: observation.id,
            };
            if (!observation.resolvedAt) {
                patch.resolvedAt = new Date();
                patch.resolvedOnPhase = observation.phase;
                patch.resolvedBy = this.me.bundleIds[0];
            }
            if (this.isValidator) {
                patch.validatedAt = patch.resolvedAt;
                patch.validatedOnPhase = patch.phase;
                patch.validatedBy = this.me.bundleIds[0];
            }
            return updateObservation(this.$route.params.projectId, patch);
        },
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.me = project.me;
            this.isValidator = project.me.allowedFeatures.includes('project_observations_MOEValidation');
            this.isEXEAllowed = project.projectFeatures.includes('project_EXE');
            this.isOPRAllowed = project.projectFeatures.includes('project_OPR');
            this.isCONAllowed = project.projectFeatures.includes('project_CON');
            this.isOPLAllowed = project.projectFeatures.includes('project_OPL');
            this.isAPAAllowed = project.projectFeatures.includes('project_OPL');
        });
    },
    data() {
        return {
            oldValue: '',
            isCONAllowed: false,
            isEXEAllowed: false,
            isOPRAllowed: false,
            isAPAAllowed: false,
            isOPLAllowed: false,
            isValidator: false,
            me: { bundleIds: [] },
        };
    },
};
</script>
